import { useMemo, useState } from 'react';
import useSWR from "swr";
import { CashIcon, OfficeBuildingIcon, TicketIcon, UserGroupIcon } from '@heroicons/react/outline';
import { useTranslation } from 'react-i18next';
import { getData, OAuthError } from '../backend';
import { useModal } from '../contexts';
import Modal from './Modal';
import SmallTextInputField from './SmallTextInputField';
import OAuthModalSalesforce from './OAuthModalSalesforce';

export interface SalesforceObject {
  name: string;
  label: string;
  custom: boolean;
}

// Standard objects analogous to the HubSpot primary objects (Contacts, Companies, Deals, Tickets)
const PRIMARY_OBJECT_ICONS: Record<string, typeof UserGroupIcon> = {
  "Opportunity": CashIcon,
  "Contact": UserGroupIcon,
  "Account": OfficeBuildingIcon,
  "Case": TicketIcon,
};

interface Props {
  onSelect: (object: SalesforceObject) => void;
}

function WorkflowModalSalesforceObjects(props: Props) {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  const { data: objects, isLoading, error } = useSWR<Array<SalesforceObject>>("/salesforce/objects/", getData, { shouldRetryOnError: false });

  const [search, setSearch] = useState("");

  const filteredObjects = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query)
      return objects ?? [];

    return (objects ?? []).filter(object => object.label.toLowerCase().includes(query) || object.name.toLowerCase().includes(query));
  }, [objects, search]);

  // Primary objects are shown in a fixed order and only if the org exposes them
  const primaryObjects = Object.keys(PRIMARY_OBJECT_ICONS)
    .map(name => (objects ?? []).find(object => object.name === name))
    .filter((object): object is SalesforceObject => !!object);

  const customObjects = filteredObjects.filter(object => object.custom);
  const otherObjects = filteredObjects.filter(object => !object.custom && !(object.name in PRIMARY_OBJECT_ICONS));

  return (
    <Modal
      title={t("Select Salesforce Object")}
      subtitle={t("Select the Salesforce object you want to use in this workflow.")}
      secondaryButtonProps={{ text: t("Cancel"), onClick: closeModal }}
    >
      <div className='flex flex-col h-[400px] gap-2'>
        {(error instanceof OAuthError) &&
          <div className='flex flex-col items-start gap-2'>
            <div className='font-semibold text-gray-400 text-sm'>
              {t("Connect Salesforce to Portant to fetch your objects")}
            </div>
            <button className='btn btn-white text-sm py-1 px-2' onClick={() => openModal(<OAuthModalSalesforce oauthUrl={error.oauthUrl} onClose={() => openModal(<WorkflowModalSalesforceObjects {...props} />)} />)}>
              {t("Connect Salesforce")}
            </button>
          </div>
        }
        {(error && !(error instanceof OAuthError)) &&
          <div className='font-semibold text-red text-sm'>
            {t("Unable to fetch objects from Salesforce, please try again.")}
          </div>
        }
        {isLoading &&
          <div className='font-semibold text-gray-400 text-sm'>
            {t("Loading")}<span className='loading-ellipse' />
          </div>
        }
        {objects &&
          <>
            {primaryObjects.length > 0 &&
              <div className='w-full flex gap-4 flex-shrink-0'>
                {primaryObjects.map((object) => {
                  const Icon = PRIMARY_OBJECT_ICONS[object.name];
                  return (
                    <div key={object.name} className='flex flex-col items-center justify-center gap-2 border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 h-32' onClick={() => props.onSelect(object)}>
                      <Icon className='w-8 h-8' />
                      {object.label}
                    </div>
                  );
                })}
              </div>
            }
            <SmallTextInputField label={t("Search")} placeholder={t("e.g. Invoice")} value={search} onChange={setSearch} autoFocus />
            <div className='flex flex-col overflow-y-scroll gap-2 pr-8'>
              {customObjects.length > 0 &&
                <div className='flex-shrink-0 font-semibold text-gray-600 mt-4'>
                  {t("Custom Objects")}
                </div>
              }
              {customObjects.map((object) =>
                <div key={object.name} className='flex items-center border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 flex-shrink-0' onClick={() => props.onSelect(object)}>
                  {object.label}
                  <span className='text-gray-400 text-xs ml-2'>{object.name}</span>
                </div>
              )}
              {otherObjects.length > 0 &&
                <div className='flex-shrink-0 font-semibold text-gray-600 mt-4'>
                  {t("Other Objects")}
                </div>
              }
              {otherObjects.map((object) =>
                <div key={object.name} className='flex items-center border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 flex-shrink-0' onClick={() => props.onSelect(object)}>
                  {object.label}
                  <span className='text-gray-400 text-xs ml-2'>{object.name}</span>
                </div>
              )}
            </div>
          </>
        }
      </div>
    </Modal>
  );
}

export default WorkflowModalSalesforceObjects;
